import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useTheme } from '../../../hooks/useTheme';
import { makeStyles } from './AndroidScreenSelectionModal.styles';
import Icon from '../../Icon/Icon';
import { AndroidDisplayInfo } from '../../../utils/secondaryDisplay';

interface AndroidScreenRowProps extends AndroidDisplayInfo {
  isSaved: boolean;
  isDisabled: boolean;
  onSelect: (displayId: number) => void;
}

const AndroidScreenRow: React.FC<AndroidScreenRowProps> = ({
  displayId,
  name,
  width,
  height,
  isPrimary,
  isCurrent,
  isSaved,
  isDisabled,
  onSelect,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const statusStyle = useMemo(() => {
    if (isCurrent) {
      return {
        iconName: 'UilCheckCircle',
        iconColor: colors.green,
        borderColor: colors.green,
        backgroundColor: colors.greenLightest,
      };
    }
    if (isSaved) {
      return {
        iconName: 'UilStar',
        iconColor: colors.states.focus,
        borderColor: colors.states.focus,
        backgroundColor: colors.orangeLightest,
      };
    }
    return {
      iconName: 'UilDesktop',
      iconColor: colors.disclosure,
      borderColor: colors.transparent,
      backgroundColor: colors.elevation1,
    };
  }, [isCurrent, isSaved, colors]);

  const badges: string[] = [];
  if (isCurrent) badges.push('Current');
  if (isPrimary) badges.push('Primary');
  if (isSaved && !isCurrent) badges.push('Saved');

  return (
    <TouchableOpacity
      onPress={() => !isDisabled && onSelect(displayId)}
      disabled={isDisabled}
      accessibilityRole="button"
      style={[
        styles.row,
        {
          borderColor: statusStyle.borderColor,
          backgroundColor: statusStyle.backgroundColor,
          opacity: isDisabled ? 0.6 : 1,
        },
      ]}
    >
      <View style={styles.screenInfo}>
        <Text style={styles.screenName}>{name}</Text>
        <Text style={styles.screenDetails}>{`${width} x ${height}`}</Text>
        {badges.length > 0 && (
          <View style={styles.badgeContainer}>
            {badges.map((badge, i) => (
              <View key={i} style={styles.badge}>
                <Text style={styles.badgeText}>{badge}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
      <Icon
        size={24}
        name={statusStyle.iconName}
        color={statusStyle.iconColor}
        style={styles.icon}
      />
    </TouchableOpacity>
  );
};

export default AndroidScreenRow;
